import { UseQueryResult } from "@tanstack/react-query";

import { Loader } from "@/components/shared/loader";
import { ResponseErrorAlert } from "@/components/shared/response-error-alert";

interface WhenAllReadyProps {
  queries: UseQueryResult[];
  loader?: React.ReactNode;
  ErrorWrapper?: React.ComponentType<{ errorEl: React.ReactNode }>;
  children: React.ReactNode;
  treatStaleRefetchAsLoading?: boolean;
}

/**
 * Renders children when all data is ready, otherwise shows loading or an error component.
 */
export function WhenAllReady({
  queries,
  loader,
  ErrorWrapper,
  children,
  treatStaleRefetchAsLoading = false,
}: WhenAllReadyProps) {
  const queryWithError = queries.find((queryResult) => queryResult.isError);

  if (queryWithError) {
    const errorEl = <ResponseErrorAlert error={queryWithError.error} />;

    if (ErrorWrapper) {
      return <ErrorWrapper errorEl={errorEl} />;
    }

    return errorEl;
  }

  const shouldBlockOnLoading = queries.some((q) => {
    if (q.isPending) return true;

    if (!treatStaleRefetchAsLoading) return false;

    return q.isFetching && q.isStale;
  });

  if (shouldBlockOnLoading) {
    return loader || <Loader />;
  }

  return children;
}
